import { BOTONES } from '../../../../../componentes/botones';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  inject,
  resource,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IDownlinkJob } from 'modelos/src';
import { DownlinkJobsService } from '../../../../../auxiliares/servicios/http/downlink-jobs.service';
import { CardColor } from '../../../../../componentes/componentes.interfaces';
import { StatCardComponent } from '../../../../../componentes/stat-card/stat-card.component';
import { LoginService } from '../../../../login/login.service';
import { NombresSeguimientoService } from '../nombres-seguimiento.service';
import {
  CADENCIA,
  Campana,
  Desglose,
  FILTRO_ENCENDIDOS,
  NIVELES_OBJETIVO,
  SELECT_JOBS_CAMPANA,
  TONO,
  agruparCampanas,
  desglose,
  duracion,
  estadoEncendido,
  faltaEstimado,
  fechaHora,
  haceCuanto,
  tituloEncendido,
} from '../seguimiento.helpers';

/**
 * Cuántos envíos se traen para armar los encendidos y apagados. Es una ventana,
 * no el histórico: agrupar se hace de este lado, y está dicho en la pantalla.
 */
const VENTANA = 3000;

interface Fila {
  c: Campana;
  d: Desglose;
  titulo: string;
  icono: string;
  porcentaje: number;
  tono: string;
  estado: string;
  iconoEstado: string;
  objetivo: string;
  falta?: string;
  cliente: string;
  jerarquia: string;
}

/** Los segmentos de la barra, en el orden en que se leen. */
const SEGMENTOS: {
  clave: keyof Desglose;
  etiqueta: string;
  tono: string;
  ayuda: string;
}[] = [
  {
    clave: 'aplicaron',
    etiqueta: 'aplicaron',
    tono: TONO.ok,
    ayuda: 'El nodo confirmó el comando.',
  },
  {
    clave: 'enCurso',
    etiqueta: 'en curso',
    tono: TONO.info,
    ayuda: 'Todavía esperan la confirmación del nodo.',
  },
  {
    clave: 'aRevisar',
    etiqueta: 'a revisar',
    tono: TONO.aviso,
    ayuda:
      'Confirmaron y no aplicaron, agotaron los envíos sin dar señal, fallaron o se abortó el envío. Piden que alguien mire el nodo.',
  },
  {
    clave: 'sinComunicacion',
    etiqueta: 'sin comunicación',
    tono: TONO.neutro,
    ayuda:
      'No se les mandó nada porque estaban fuera de línea: pasaron su tiempo límite de comunicación (24 h si no tienen uno). Ya están avisadas por el evento de comunicación.',
  },
  {
    clave: 'reemplazados',
    etiqueta: 'reemplazadas',
    tono: TONO.tenue,
    ayuda:
      'Llegó otro encendido o apagado más nuevo para la misma luminaria. No es una falla.',
  },
];

/**
 * Los encendidos y apagados manuales, a una luminaria o a un grupo, y cuántas
 * luminarias los aplicaron. Se muestran **todos**, también los de una sola
 * luminaria: es lo que el operador viene a confirmar después de apretar el
 * botón.
 */
@Component({
  selector: 'app-listado-encendidos',
  standalone: true,
  imports: [
    DecimalPipe,
    NgTemplateOutlet,
    BOTONES,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    RouterLink,
    StatCardComponent,
  ],
  templateUrl: './listado-encendidos.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ListadoEncendidosComponent {
  private readonly http = inject(DownlinkJobsService);
  private readonly nombres = inject(NombresSeguimientoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly query = toSignal(this.route.queryParamMap, {
    initialValue: undefined,
  });

  protected readonly puedeVerHijos =
    LoginService.getCliente()?.tipoCliente !== 'Final';

  protected readonly hijos = computed(
    () => this.puedeVerHijos && this.query()?.get('hijos') !== '0',
  );

  protected readonly ventana = VENTANA;
  protected readonly SEGMENTOS = SEGMENTOS;

  constructor() {
    const intervalo = setInterval(() => {
      if (document.visibilityState === 'visible') this.jobs.reload();
    }, CADENCIA.listado * 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(intervalo));
  }

  protected readonly jobs = resource({
    params: () => ({ hijos: this.hijos() }),
    loader: ({ params }) =>
      this.http.getFiltered({
        filter: JSON.stringify(FILTRO_ENCENDIDOS),
        sort: '-fechaCreacion',
        limit: VENTANA,
        includeChildren: params.hijos || undefined,
        // El total no se usa: lo que se muestra son acciones, no envíos.
        excludeTotalCount: true,
        select: `${SELECT_JOBS_CAMPANA} descripcion idCliente`,
      }),
  });

  private readonly filas = computed<Fila[]>(() => {
    const jobs = this.jobs.value()?.datos ?? [];
    const porEjecucion = new Map<string, IDownlinkJob[]>();
    for (const j of jobs) {
      if (!j.idEjecucion) continue;
      const lista = porEjecucion.get(j.idEjecucion) ?? [];
      lista.push(j);
      porEjecucion.set(j.idEjecucion, lista);
    }
    return agruparCampanas(jobs).map((c) =>
      this.fila(c, porEjecucion.get(c.id) ?? []),
    );
  });

  protected readonly enCurso = computed(() =>
    this.filas().filter((f) => !f.c.terminada),
  );
  protected readonly terminadas = computed(() =>
    this.filas().filter((f) => f.c.terminada),
  );

  protected readonly kpis = computed(() => {
    const todas = this.filas();
    const enCurso = todas.filter((f) => !f.c.terminada);
    return [
      {
        etiqueta: 'En curso',
        valor: enCurso.length,
        icono: 'pending',
        color: 'blue' as CardColor,
        ayuda:
          'Encendidos y apagados con alguna luminaria que todavía no confirmó.',
      },
      {
        etiqueta: 'Luminarias esperando',
        valor: enCurso.reduce((s, f) => s + f.d.enCurso, 0),
        icono: 'hourglass_top',
        color: 'indigo' as CardColor,
        ayuda:
          'Suma de las luminarias que todavía no confirmaron, en los que siguen en curso.',
      },
      {
        etiqueta: 'Con luminarias a revisar',
        valor: todas.filter((f) => f.d.aRevisar).length,
        icono: 'flag',
        color: 'orange' as CardColor,
        ayuda:
          'Encendidos y apagados con alguna luminaria que confirmó y no aplicó, agotó los envíos, falló o se abortó. Piden una decisión.',
      },
      {
        etiqueta: 'Terminados',
        valor: todas.filter((f) => f.c.terminada).length,
        icono: 'task_alt',
        color: 'green' as CardColor,
        ayuda: 'Ninguna luminaria espera nada.',
      },
    ];
  });

  protected cuando(iso: string): string {
    return haceCuanto(iso);
  }

  protected fecha(iso: string): string {
    return fechaHora(iso);
  }

  protected ancho(f: Fila, clave: keyof Desglose): number {
    return f.c.nodos ? (f.d[clave] / f.c.nodos) * 100 : 0;
  }

  protected recargar(): void {
    this.jobs.reload();
  }

  protected cambiarHijos(ver: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { hijos: ver ? null : '0' },
      queryParamsHandling: 'merge',
    });
  }

  private fila(c: Campana, lote: readonly IDownlinkJob[]): Fila {
    const d = desglose(c);
    const e = estadoEncendido(c, d);
    const ms = faltaEstimado(c);
    const idCliente = lote.find((j) => j.idCliente)?.idCliente;
    return {
      c,
      d,
      titulo: tituloEncendido(c, lote),
      icono: c.nombre === 'Apagar' ? 'light_off' : 'lightbulb',
      porcentaje: Math.round(c.progreso * 100),
      tono: e.tono,
      estado: e.titulo,
      iconoEstado: e.icono,
      objetivo: objetivo(c),
      falta: ms === undefined ? undefined : duracion(ms),
      cliente: this.nombres.nombreCliente(idCliente),
      jerarquia: this.nombres.jerarquiaCliente(idCliente),
    };
  }
}

function objetivo(c: Campana): string {
  const lums =
    c.nodos === 1
      ? '1 luminaria'
      : `${c.nodos.toLocaleString('es-AR')} luminarias`;
  if (!c.objetivo) return lums;
  const nivel = NIVELES_OBJETIVO[c.objetivo.nivel] ?? c.objetivo.nivel;
  return c.objetivo.nombre
    ? `${nivel} ${c.objetivo.nombre} · ${lums}`
    : `${nivel} · ${lums}`;
}
